import { useMemo } from 'react'
import { useRecoilValue, useRecoilValueLoadable } from 'recoil'

import {
  useEntityBaseMetadata,
  useEntityBaseMetadataSync,
  useFormBaseMetadata,
  useFormBaseMetadataSync,
  useModuleBaseMetadata,
  useViewBaseMetadata,
  useViewBaseMetadataSync,
} from '../base-metadata'
import { $model } from './selectors'

/**
 * The function `useEntityMetadata` returns the metadata of an entity based on its name.
 * @param {string | undefined | null} entityName - The `entityName` parameter is a string that
 * represents the name of an entity. It can be `undefined`, `null`, or a valid string value.
 * @returns the `entityModel` object, which is found by filtering the `model.entities` array based on
 * the `name` property matching the `entityName` parameter.
 */
export function useEntityMetadata(entityName: string | undefined | null) {
  const entityMetadata = useEntityBaseMetadata(entityName as string)
  const model = useRecoilValue($model(entityMetadata?.msProvider as string))
  return useMemo(() => {
    const entityModel = model.entities.find((e) => e.name === entityName)
    return entityModel
  }, [model.entities, entityName])
}

/**
 * Retrieves the entity model of an entity looking for it in the external references
 * in case it can't find the owner
 */
export function useEntityMetadataIncludeExternal(
  entityName: string | undefined | null,
  moduleName: string | undefined | null,
) {
  const entityMetadata = useModuleBaseMetadata(entityName as string, moduleName as string)
  const model = useRecoilValue($model(entityMetadata?.msProvider as string))
  return useMemo(() => {
    let entityModel = model.entities.find((e) => e.name === entityName)
    if (!entityModel) entityModel = model.getExternalEntity(entityName as string, entityMetadata?.msProvider as string)
    return entityModel
  }, [model.entities, entityName])
}

/**
 * The function `useEntityMetadataSync` is a TypeScript function that synchronously retrieves entity
 * metadata based on the provided entity name.
 * @param {string | undefined | null} entityName - The `entityName` parameter is a string that
 * represents the name of an entity. It can be `undefined`, `null`, or a valid string value.
 * @returns The function `useEntityMetadataSync` returns the `entityModel` object if the `result.state`
 * is `'hasValue'`, otherwise it returns `undefined`. If the `result.state` is `'hasError'`, it throws
 * an error.
 */
export function useEntityMetadataSync(entityName: string | undefined | null) {
  const entityMetadata = useEntityBaseMetadataSync(entityName as string)
  const result = useRecoilValueLoadable($model(entityMetadata?.msProvider as string))
  return useMemo(() => {
    switch (result.state) {
      case 'hasValue': {
        const entityModel = result.contents.entities.find((e) => e.name === entityName)
        return entityModel
      }
      case 'hasError':
        throw result.contents
      case 'loading':
        return undefined
    }
  }, [result, entityName])
}

/**
 * The function `useViewMetadata` returns the metadata of a specific view for a given entity in a
 * TypeScript codebase.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of an
 * entity. It is used to retrieve the metadata for that entity.
 * @param {string} viewName - The `viewName` parameter is a string that represents the name of the view
 * you want to retrieve from the entity.
 * @returns the viewModel, which is obtained by finding the entityModel with the given entityName and
 * then getting the view with the given viewName from that entityModel.
 */
export function useViewMetadata(entityName: string, viewName: string) {
  const entityMetadata = useViewBaseMetadata(entityName, viewName)
  const model = useRecoilValue($model(entityMetadata?.msProvider as string))
  return useMemo(() => {
    let entityModel = model.entities.find((e) => e.name === entityName)
    if (!entityModel) entityModel = model.getExternalEntity(entityName, entityMetadata?.msProvider as string)
    const viewModel = entityModel?.getView(viewName)
    return viewModel
  }, [model.entities, entityName, viewName])
}

/**
 * The function `useViewMetadataSync` is a TypeScript function that synchronously retrieves metadata
 * for a specific view of an entity and returns the view model if it exists.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of an
 * entity. It is used to retrieve the metadata and model information for that entity.
 * @param {string} viewName - The `viewName` parameter is a string that represents the name of a view
 * within an entity. It is used to retrieve the metadata of that specific view.
 * @returns The function `useViewMetadataSync` returns the metadata of a specific view for a given
 * entity.
 */
export function useViewMetadataSync(entityName: string, viewName: string) {
  const entityMetadata = useViewBaseMetadataSync(entityName, viewName)
  const result = useRecoilValueLoadable($model(entityMetadata?.msProvider as string))
  return useMemo(() => {
    switch (result.state) {
      case 'hasValue': {
        let entityModel = result.contents.entities.find((e) => e.name === entityName)
        if (!entityModel)
          entityModel = result.contents.getExternalEntity(entityName, entityMetadata?.msProvider as string)
        const viewModel = entityModel?.getView(viewName)
        return viewModel
      }
      case 'hasError':
        throw result.contents
      case 'loading':
        return undefined
    }
  }, [result, entityName, viewName])
}

/**
 * The function `useFormMetadata` returns the form model for a given entity and form name, based on the
 * entity metadata and model obtained from Recoil state.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of an
 * entity. It is used to retrieve the entity model from the `model` object.
 * @param {string} formName - The `formName` parameter is a string that represents the name of the
 * form. It is used to retrieve the form model from the entity model.
 * @returns the form model for the specified entity and form name.
 */
export function useFormMetadata(entityName: string, formName: string) {
  const entityMetadata = useFormBaseMetadata(entityName, formName)

  const model = useRecoilValue($model(entityMetadata?.msProvider as string))
  return useMemo(() => {
    let entityModel = model.entities.find((e) => e.name === entityName)
    if (!entityModel) entityModel = model.getExternalEntity(entityName, entityMetadata?.msProvider as string)
    const formModel = entityModel?.getForm(formName)
    return formModel
  }, [model.entities, entityName, formName])
}

/**
 * The function `useFormMetadataSync` retrieves and returns the form metadata for a specific entity and
 * form name.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of an
 * entity. It is used to retrieve the metadata for the specified entity.
 * @param {string} formName - The `formName` parameter is a string that represents the name of the
 * form. It is used to retrieve the form model from the entity model.
 * @returns The function `useFormMetadataSync` returns the `formModel` object.
 */
export function useFormMetadataSync(entityName: string, formName: string) {
  const entityMetadata = useFormBaseMetadataSync(entityName, formName)

  const result = useRecoilValueLoadable($model(entityMetadata?.msProvider as string))
  return useMemo(() => {
    switch (result.state) {
      case 'hasValue': {
        const model = result.contents
        let entityModel = model.entities.find((e) => e.name === entityName)
        if (!entityModel) entityModel = model.getExternalEntity(entityName, entityMetadata?.msProvider as string)
        const formModel = entityModel?.getForm(formName)
        return formModel
      }
      case 'hasError':
        throw result.contents
      case 'loading':
        return undefined
    }
  }, [result, entityName, formName])
}

/**
 * The function `useMessagesMetadata` returns the messages metadata from the specified message
 * provider.
 * @param {string} msProvider - The `msProvider` parameter is a string that represents the provider of
 * the messages. It is used to retrieve the model associated with that provider using the
 * `useRecoilValue` hook.
 * @returns the `model.messages` value.
 */
export function useMessagesMetadata(msProvider: string) {
  const model = useRecoilValue($model(msProvider))
  return useMemo(() => {
    return model.messages
  }, [model])
}

/**
 * The function `useOptionsetMetadata` returns the option set metadata for a given MS provider and
 * family.
 * @param {string} msProvider - The `msProvider` parameter is a string that represents the provider of
 * the metadata. It could be a specific service or system that provides the metadata.
 * @param {string} family - The "family" parameter is a string that represents the name or identifier
 * of the option set family. It is used to retrieve the option set from the model.
 * @returns the option set metadata for a given MS provider and family.
 */
export function useOptionsetMetadata(msProvider: string, family: string) {
  const model = useRecoilValue($model(msProvider))
  return useMemo(() => {
    return model.getOptionSet(family)
  }, [model])
}
